import { useState, useEffect } from 'react'
import { requestMobile } from '@/lib/request'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { RefreshCw, Search, X } from 'lucide-react'
import { usePersistentState } from '@/hooks/usePersistentState'

interface WithdrawItem {
  order_no: string
  name: string
  bank_name: string
  bank_account_name?: string
  bank_account?: string
  withdraw_money: number
  withdraw_time: string
  status: string
}

export default function Withdraw() {
  const [list, setList] = useState<WithdrawItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNum, setPageNum] = useState(1)
  const [hasMore, setHasMore] = useState(true)

  const [nameSearch, setNameSearch] = usePersistentState('mobile:withdraw:search-input', '')
  const [appliedName, setAppliedName, clearAppliedName] = usePersistentState(
    'mobile:withdraw:search',
    '',
  )

  const buildFilter = (name: string) => {
    const filter: Record<string, any> = {}
    if (name.trim()) {
      filter.name = { $regex: `.*${name.trim()}.*` }
    }
    return filter
  }

  const fetchData = async (page: number = 1, name = appliedName) => {
    setLoading(true)
    try {
      const resp = await requestMobile('/system/api/query/TX', {
        page_num: page,
        page_size: 20,
        query_filter: buildFilter(name),
      })
      if (resp && !resp.code) {
        if (page === 1) {
          setList(resp.data.list)
        } else {
          setList((prev) => [...prev, ...resp.data.list])
        }
        setHasMore(resp.data.list.length === 20)
        setPageNum(page)
      }
    } catch (error) {
      console.error('Failed to fetch withdraw list:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData(1)
  }, [])

  const handleSearch = () => {
    setAppliedName(nameSearch)
    fetchData(1, nameSearch)
  }

  const handleClear = () => {
    setNameSearch('')
    clearAppliedName()
    fetchData(1, '')
  }

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchData(pageNum + 1)
    }
  }

  const hasActiveFilter = !!appliedName

  return (
    <div className="min-h-full bg-background">
      {/* 头部 */}
      <div
        className="bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60 border-b"
        style={{ paddingTop: 'max(env(safe-area-inset-top), 16px)' }}
      >
        <div className="flex items-center justify-between px-4 pt-2 pb-3">
          <div>
            <h1 className="text-xl font-bold">提现记录</h1>
            <p className="text-sm text-muted-foreground">{list.length} 条记录</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => fetchData(1)} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* 搜索栏 */}
        <div className="px-4 pb-3 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              className="pl-8 h-8 text-sm"
              placeholder="搜索账号名..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
          </div>
          <Button size="sm" className="h-8 px-3" onClick={handleSearch}>
            搜索
          </Button>
          {hasActiveFilter && (
            <Button size="sm" variant="ghost" className="h-8 px-2" onClick={handleClear}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

      </div>

      {/* 列表 */}
      <div className="p-4 space-y-3">
        {list.map((item) => (
          <Card key={item.order_no} className="p-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-medium">{item.name}</span>
                <span className="text-sm">{item.status}</span>
              </div>
              <div className="text-sm text-muted-foreground space-y-0.5">
                <p>{item.bank_name}  {item.withdraw_time}</p>
                <p>
                  金额：
                  <span className="text-foreground font-medium">¥{item.withdraw_money}</span>
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {hasMore && (
        <div className="mt-2 mb-4 text-center">
          <Button variant="outline" size="sm" onClick={handleLoadMore} disabled={loading}>
            {loading ? '加载中...' : '加载更多'}
          </Button>
        </div>
      )}

      {!hasMore && list.length > 0 && (
        <div className="mt-2 mb-4 text-center text-sm text-muted-foreground">没有更多数据了</div>
      )}

      {list.length === 0 && !loading && (
        <div className="mt-8 text-center text-muted-foreground">
          {hasActiveFilter ? '没有符合条件的记录' : '暂无提现记录'}
        </div>
      )}
    </div>
  )
}
